import { useMemo } from "react";

import useDataTable from "common/components/DataTable/useDataTable";
import ProfileHeading from "common/components/ProfileHeading";
import Skeleton from "common/components/ui/Skeleton";
import { typedObjectEntries } from "common/helpers/utils";
import type { Lead } from "common/types";

import FavoriteLead from "../components/FavoriteLead";
import LeadNotesIndicator from "../components/LeadNotesIndicator";
import { LEADS_COLUMNS } from "../constants";

import type { ColumnDef, VisibilityState } from "@tanstack/react-table";

const DEFAULT_COLUMN_CLASS = "min-w-40 max-w-40";
const DEFAULT_LOADER_CLASS = "h-4 w-32";

type UseLeadsDataTableProps = {
  leads: Lead[];
  isLoading: boolean;
  pageSize: number;
  openSidebar?: (leadId: string, action: "preview" | "notes") => void;
  columnVisibility?: VisibilityState;
  setColumnVisibility?: (columnVisibility: VisibilityState) => void;
} & ( // selection is only enabled when both the selection and its setter are passed
  | {
      selectedLeads: Lead[];
      setSelectedLeads: (selectedLeads: Lead[]) => void;
    }
  | { selectedLeads?: never; setSelectedLeads?: never }
);

export default function useLeadsDataTable({
  leads,
  isLoading,
  pageSize,
  openSidebar,
  columnVisibility,
  setColumnVisibility,
  selectedLeads,
  setSelectedLeads,
}: UseLeadsDataTableProps) {
  const columns = useMemo<ColumnDef<Lead>[]>(() => {
    return [
      {
        header: "Leads",
        id: "account",
        cell: ({ row: { original } }) => {
          if (isLoading) {
            return <ProfileHeading isLoading />;
          }

          const { id, picture_url, favorite, full_name, notes, occupation } =
            original;

          return (
            <ProfileHeading
              fullName={full_name}
              pictureUrl={picture_url}
              occupation={occupation}
              extraComponent={
                <div className="flex gap-x-2.5 items-center">
                  <LeadNotesIndicator
                    noteCount={notes.length}
                    onClick={
                      openSidebar ? () => openSidebar(id, "notes") : undefined
                    }
                  />
                  <FavoriteLead leadId={id} isFavorite={favorite} />
                </div>
              }
            />
          );
        },
        enableHiding: false,
      },
      ...typedObjectEntries(LEADS_COLUMNS).map(
        ([id, { label, icon, renderTag }]): ColumnDef<Lead> => {
          let className = DEFAULT_COLUMN_CLASS;
          let loaderClassName = DEFAULT_LOADER_CLASS;

          if (id === "accounts") {
            loaderClassName = "h-6 w-28 rounded-full";
          } else if (id === "labels") {
            className = "min-w-56 max-w-56";
            loaderClassName = "h-6 w-20";
          }

          return {
            id,
            header: label,
            cell: ({ row: { original } }) =>
              isLoading ? (
                <Skeleton className={loaderClassName} />
              ) : (
                renderTag(original, true)
              ),
            meta: { icon, className },
          };
        },
      ),
    ];
  }, [isLoading, openSidebar]);

  return useDataTable(
    {
      data: leads || new Array(pageSize).fill({}),
      columns,
    },
    {
      ...(setSelectedLeads && {
        selectedRows: selectedLeads,
        onRowSelectionChange: setSelectedLeads,
      }),
      columnVisibility,
      onColumnVisibilityChange: setColumnVisibility,
    },
  );
}
